import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import { useId, useState } from "react";
import { tournamentTerm } from "@bw/shared/tournaments";
import type { TournamentTermKey } from "@bw/shared/tournaments";

import { useGlossary } from "./glossary-drawer";
import { STAFF_FONT, tone } from "./ui";

/** An invisible 44 px hit area around a small round ⓘ, for touch. */
export const INFO_HIT_AREA = {
  position: "relative",
  "&::before": { content: '""', position: "absolute", inset: "-14px" },
} as const;

/** ⓘ next to a term: opens a focusable popover with the organizer wording and
 * a link to every tournament term (keyboard, mouse and touch alike). */
export function TermHelp({
  term,
  label,
  description,
}: {
  term: TournamentTermKey;
  label?: string;
  description?: string;
}) {
  const glossary = useGlossary();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const titleId = useId();
  const entry = tournamentTerm(term);
  const text = description ?? entry.staff;
  return (
    <>
      <Box
        aria-expanded={Boolean(anchor)}
        aria-haspopup="dialog"
        aria-label={label ?? `What is ${entry.label.toLowerCase()}?`}
        component="button"
        onClick={(event: React.MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
        sx={{
          ...INFO_HIT_AREA,
          width: 16,
          height: 16,
          borderRadius: "50%",
          border: `1.5px solid ${tone.faint}`,
          color: tone.subtle,
          font: "inherit", fontWeight: 700, fontSize: 10,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "transparent",
          p: 0,
          cursor: "pointer",
          lineHeight: 1,
          flex: "none",
          verticalAlign: "-3px",
          "&:hover, &:focus-visible": { borderColor: tone.brand, color: tone.brand, bgcolor: tone.brandSoft, outline: "none", boxShadow: `0 0 0 3px ${tone.brandRing}` },
        }}
        type="button"
      >
        i
      </Box>
      <Popover
        anchorEl={anchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={() => setAnchor(null)}
        open={Boolean(anchor)}
        slotProps={{
          paper: {
            "aria-labelledby": titleId,
            role: "dialog",
            sx: {
              bgcolor: tone.ink,
              color: tone.onInk,
              borderRadius: "8px",
              p: "10px 12px",
              fontFamily: STAFF_FONT,
              fontSize: 12.5,
              lineHeight: 1.45,
              maxWidth: 300,
              boxShadow: tone.popShadow,
            },
          } as object,
        }}
      >
        <Box component="b" id={titleId} sx={{ color: tone.onInkStrong }}>
          {entry.label}
        </Box>
        {": "}
        {text}{" "}
        {glossary ? (
          <Box
            component="button"
            onClick={() => {
              setAnchor(null);
              glossary.open(entry.label);
            }}
            sx={{ background: "none", border: 0, p: 0, color: tone.onInkLink, textDecoration: "underline", font: "inherit", cursor: "pointer" }}
            type="button"
          >
            All tournament terms
          </Box>
        ) : null}
      </Popover>
    </>
  );
}
